import React, {useEffect} from 'react';
import {Redirect, Route, Switch, withRouter} from "react-router-dom";
import {CircularProgress} from "@mui/material";
import {useBootstrap, useEntities, useEntity, useFilters, useProgress} from "@atttomyx/shared-hooks";
import {
    AboutPage, AddUserPage,
    ChangePasswordPage, EditUserPage,
    ForgotPasswordPage,
    JoinAccountPage,
    LoginPage,
    MyAccountPage,
    NotFoundPage,
    NotificationsPage,
    ProfilePage,
    PublicHeader,
    Splash, UserCard, UserFilters, UsersPage,
    WelcomePage
} from "@atttomyx/react-components";
import Footer from "./components/footer/footer";
import UserSettingsForm from "./forms/userSettingsForm/userSettingsForm";
import ChargesPage from "./pages/chargesPage/chargesPage";
import CreditCardPage from "./pages/creditCardPage/creditCardPage";
import * as appService from "./services/app";
import * as authService from "./services/auth";
import * as billingService from "./services/billing";
import * as accountService from "./services/account";
import * as notificationService from "./services/notifications";
import * as profileService from "./services/profile";
import * as userService from "./services/user";
import {notifications as notificationUtils, sorting} from "@atttomyx/shared-utils";
import {branding as brandingUtils, router} from "@atttomyx/react-utils";
import {isFailure, sortByNextBillDueAndReason} from "./utils/charges";
import {hasCard} from "./utils/cards";
import {themes, time} from "@atttomyx/shared-constants";
import {USER_FILTERS} from "./filters";
import {
    APP_NAME,
    APP_TAG_LINE,
    NOTIFICATION_TYPES,
    PAGE_ABOUT,
    PAGE_ACCOUNT,
    PAGE_CARD,
    PAGE_CHARGES,
    PAGE_HOME,
    PAGE_JOIN_ACCOUNT,
    PAGE_LOGIN,
    PAGE_NOTIFICATIONS,
    PAGE_PASSWORD,
    PAGE_PROFILE,
    PAGE_RECOVERY, PAGE_USER, PAGE_USERS,
    PAGE_WELCOME,
    PRIVATE_PAGES_EXACT,
    PRIVATE_PAGES_STARTS_WITH,
    PUBLIC_PAGES_EXACT,
    PUBLIC_PAGES_STARTS_WITH,
    TOPICS,
} from "./constants";
import icon from "./icon.png";

const App = props => {
    const {dimensions, snackbar, history, onThemeChange} = props;
    const notifications = useEntities(notificationService.listNotifications, snackbar.setError, "notifications", sorting.sortByCreatedDesc);
    const charges = useEntities(billingService.findCharges, snackbar.setError, "charges", sortByNextBillDueAndReason);
    const payments = useEntities(billingService.findPayments, snackbar.setError, "payments", sorting.sortByCreatedDesc);
    const card = useEntity(billingService.findCard, snackbar.setError);
    const users = useEntities(userService.listUsers, snackbar.setError, "users", sorting.sortByLastNameAndFirstName);

    const userFilters = useFilters(USER_FILTERS);

    const clearAll = () => {
        progress.clear();
        bootstrap.clear();
        notifications.clear();
        charges.clear();
        payments.clear();
        card.clear();
        users.clear();
        userFilters.clear();
    };

    const onLogout = () => {
        authService.cancelAllRequests("logged out");
        authService.clearAuthToken();

        clearAll();

        router.redirectToInitialPage(history, PUBLIC_PAGES_EXACT, PUBLIC_PAGES_STARTS_WITH, PAGE_LOGIN);
    };

    const onSwitchAccounts = (accounts) => {
        clearAll();
        onLogin(accounts);
        router.redirectTo(history, PAGE_HOME);
    };

    const bootstrapSuccess = () => {
        if (bootstrap.user.temporary) {
            router.redirectTo(history, PAGE_PASSWORD);

        } else {
            router.redirectToInitialPage(history, PRIVATE_PAGES_EXACT, PRIVATE_PAGES_STARTS_WITH, PAGE_HOME);
        }

        notifications.refresh();
        charges.refresh();
        payments.refresh();
        card.refresh();

        if (bootstrap.user.roles.admin) {
            users.refresh();
        } else {
            users.empty();
        }

        authService.recordLoginIfNecessary(snackbar.setError);
    };

    const bootstrapFailure = (err) => {
        snackbar.setError(err);
        onLogout();
    };

    const bootstrap = useBootstrap(authService, accountService, profileService, notificationService,
        bootstrapSuccess, bootstrapFailure);

    const progress = useProgress(bootstrap,
        [notifications, charges, payments],
        [card, users],
        0,
        time.MILLISECONDS_IN_SECOND);

    const onLogin = (accounts) => {
        progress.refresh();
        bootstrap.refresh(accounts);
    };

    useEffect(() => {
        const settings = bootstrap.user ? bootstrap.user.settings || {} : {};
        const theme = settings.theme || themes.DARK;
        const branding = brandingUtils.getBranding(theme);

        onThemeChange(theme, branding);
    }, [bootstrap.user]);

    useEffect(() => {
        appService.ensureOnLatestVersion(false, null);
        authService.setupAxiosInterceptors(onLogout);
        notificationUtils.initNotifications(NOTIFICATION_TYPES, TOPICS);

        if (authService.loggedIn()) {
            onLogin();

        } else {
            onLogout();
        }
    }, []);

    return <div className="app">
        {progress.loading || progress.paused ?
            <Splash
                percent={progress.percent}
            /> : bootstrap.user ? charges.loading || payments.loading || card.loading ?
            <div className="content">
                <CircularProgress color="primary" size="40px"/>
            </div> :
            <>
                <div className="content">
                    <Switch>
                        <Route exact path="/">
                            <Redirect to={PAGE_CHARGES}/>
                        </Route>
                        <Route exact path={PAGE_HOME}>
                            <Redirect to={PAGE_CHARGES}/>
                        </Route>
                        <Route path={PAGE_CHARGES}>
                            <ChargesPage
                                dimensions={dimensions}
                                snackbar={snackbar}
                                charges={charges.entities}
                                payments={payments.entities}
                                hasCard={hasCard(card.entity)}
                                onPaid={(payment) => {
                                    charges.refresh();
                                    payments.refresh();
                                }}
                            />
                        </Route>
                        <Route path={PAGE_CARD}>
                            <CreditCardPage
                                snackbar={snackbar}
                                card={card.entity}
                                onSaveCard={(savedCard) => {
                                    card.onSave(savedCard);
                                }}
                                onDeleteCard={(cardId) => {
                                    card.onDelete();
                                }}
                                onSavePayment={(payment) => {
                                    charges.refresh();
                                    payments.refresh();
                                }}
                            />
                        </Route>
                        <Route path={PAGE_NOTIFICATIONS}>
                            <NotificationsPage
                                dimensions={dimensions}
                                snackbar={snackbar}
                                notifications={notifications.entities}
                                onBulkSave={notifications.onEntitiesSaved}
                                onDelete={notifications.onEntityDeleted}
                                notificationService={notificationService}
                            />
                        </Route>
                        {bootstrap.user.roles.admin ?
                            <Route path={PAGE_ACCOUNT}>
                                <MyAccountPage
                                    snackbar={snackbar}
                                    account={bootstrap.account}
                                    onSave={bootstrap.syncAccount}
                                    myAccountService={accountService}
                                    showCode={true}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route path={PAGE_USERS}>
                                <UsersPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={bootstrap.user}
                                    users={users.entities}
                                    filters={userFilters}
                                    onDelete={users.onEntityDeleted}
                                    onGotoAdd={() => router.redirectTo(history, PAGE_USER)}
                                    onGotoProfile={() => router.redirectTo(history, PAGE_PROFILE)}
                                    onGotoUser={(userId) => router.redirectTo(history, `${PAGE_USER}/${userId}`)}
                                    userService={userService}
                                    userCard={UserCard}
                                    userFilters={UserFilters}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route exact path={PAGE_USER}>
                                <AddUserPage
                                    snackbar={snackbar}
                                    onSave={(user) => {
                                        users.onEntitySaved(user);
                                        router.redirectTo(history, PAGE_USER + "/" + user.id);
                                    }}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route
                                path={`${PAGE_USER}/:id`}
                                render={(props) =>
                                    <EditUserPage
                                        {...props}
                                        snackbar={snackbar}
                                        user={bootstrap.user}
                                        users={users.entities}
                                        filters={userFilters}
                                        onSave={users.onEntitySaved}
                                        onPrev={(userId) => router.redirectTo(history, PAGE_USER + "/" + userId)}
                                        onNext={(userId) => router.redirectTo(history, PAGE_USER + "/" + userId)}
                                    />}
                            /> : null}
                        <Route path={PAGE_PROFILE}>
                            <ProfilePage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                onSaveProfile={bootstrap.syncProfile}
                                onSaveNotifications={bootstrap.syncNotifications}
                                profileService={profileService}
                                notificationService={notificationService}
                                settingsForm={UserSettingsForm}
                                showImage={true}
                                showBirthday={true}
                                allowApp={true}
                                allowEmail={true}
                                onDeleteProfile={onLogout}
                            />
                        </Route>
                        <Route path={PAGE_PASSWORD}>
                            <ChangePasswordPage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                authService={authService}
                                onSaveProfile={bootstrap.syncProfile}
                                onToHome={() => router.redirectTo(history, PAGE_HOME)}
                            />
                        </Route>
                        <Route path={PAGE_JOIN_ACCOUNT}>
                            <JoinAccountPage
                                snackbar={snackbar}
                                accounts={bootstrap.accounts}
                                onSwitchAccounts={onSwitchAccounts}
                                authService={authService}
                                accountService={accountService}
                            />
                        </Route>
                        <Route path={PAGE_ABOUT}>
                            <AboutPage
                                src={icon}
                                appName={APP_NAME}
                                appDescription={APP_TAG_LINE}
                                appService={appService}
                                user={bootstrap.user}
                            />
                        </Route>
                        <Route>
                            <NotFoundPage/>
                        </Route>
                    </Switch>
                </div>
                <Footer
                    snackbar={snackbar}
                    numNotifications={notifications.entities.filter(notificationUtils.isNewNotification).length}
                    numFailedCharges={charges.entities.filter(isFailure).length}
                    hasCard={hasCard(card.entity)}
                    onLogout={onLogout}
                    bootstrap={bootstrap}
                    authService={authService}
                    onSwitchAccounts={onSwitchAccounts}
                />
            </> :
            <>
                <PublicHeader
                    dimensions={dimensions}
                    appName={APP_NAME}
                    src={icon}
                />
                <div className="content">
                    <Switch>
                        <Route path={PAGE_LOGIN}>
                            <LoginPage
                                snackbar={snackbar}
                                authService={authService}
                                onForgotPassword={() => router.redirectTo(history, PAGE_RECOVERY)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={PAGE_RECOVERY}>
                            <ForgotPasswordPage
                                snackbar={snackbar}
                                authService={authService}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                                appName={APP_NAME}
                            />
                        </Route>
                        <Route path={PAGE_WELCOME}>
                            <WelcomePage
                                snackbar={snackbar}
                                authService={authService}
                                onToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                            />
                        </Route>
                    </Switch>
                </div>
            </>}
    </div>
}

export default withRouter(App);
